// Simple, predictable layout system for audio player
import React from 'react';

// Simple breakpoints based on container width
// Updated to accommodate previous/next card navigation buttons
export const SIMPLE_BREAKPOINTS = {
  TWO_LINE: 300,    // Two-line layout threshold (increased by 60px)
  VERY_SMALL: 340,  // Absolute minimum single line (increased by 60px)
  SMALL: 460,       // Compact layout (increased by 60px)
  MEDIUM: 670,      // Standard layout (increased by 70px)
  LARGE: 880        // Full layout (increased by 80px)
};

// Simple layout configurations - all elements always visible, space compresses naturally
export const SIMPLE_LAYOUTS = {
  twoLine: {
    name: 'twoLine',
    showTimeDisplay: true,
    showVolumeSlider: false,
    showDownloadButton: true,
    showSpeedControl: true,
    buttonSize: 'small',
    spacing: 0.5,
    padding: { py: 0.5, px: 1 },
    timeDisplayWidth: 70,
    volumeSliderWidth: 0,
    isMultiLine: true,
    // Two-line specific layout
    firstLineElements: ['playback', 'time'],
    secondLineElements: ['controls']
  },
  verySmall: {
    name: 'verySmall',
    showTimeDisplay: true,  // Always show, but compressed
    showVolumeSlider: false, // Hide slider but keep volume button
    showDownloadButton: true, // Always show
    showSpeedControl: true,
    buttonSize: 'small',
    spacing: 0.25, // Tight spacing
    padding: { py: 0.5, px: 1 },
    timeDisplayWidth: 60, // Compressed time display
    volumeSliderWidth: 0   // No slider
  },
  small: {
    name: 'small', 
    showTimeDisplay: true,
    showVolumeSlider: true, // Show slider
    showDownloadButton: true,
    showSpeedControl: true,
    buttonSize: 'small',
    spacing: 0.5,
    padding: { py: 0.75, px: 1.5 },
    timeDisplayWidth: 80,
    volumeSliderWidth: 40 // Small slider width
  },
  medium: {
    name: 'medium',
    showTimeDisplay: true,
    showVolumeSlider: true, // Show slider
    showDownloadButton: true,
    showSpeedControl: true,
    buttonSize: 'medium',
    spacing: 0.75,
    padding: { py: 1, px: 2 },
    timeDisplayWidth: 100,
    volumeSliderWidth: 60
  },
  large: {
    name: 'large',
    showTimeDisplay: true,
    showVolumeSlider: true,
    showDownloadButton: true,
    showSpeedControl: true,
    buttonSize: 'large',
    spacing: 1,
    padding: { py: 1, px: 2 },
    timeDisplayWidth: 120,
    volumeSliderWidth: 80
  }
};

// Simple button sizes
export const SIMPLE_BUTTON_SIZES = {
  small: {
    skip: 24,
    play: 28,
    control: 24,
    iconSize: 'small'
  },
  medium: {
    skip: 28,
    play: 32,
    control: 28,
    iconSize: 'small'
  },
  large: {
    skip: 32,
    play: 36,
    control: 32,
    iconSize: 'medium'
  }
};

// Get layout configuration based on container width
export const getSimpleLayout = (containerWidth) => {
  let layoutKey = 'twoLine';
  
  if (containerWidth >= SIMPLE_BREAKPOINTS.LARGE) {
    layoutKey = 'large';
  } else if (containerWidth >= SIMPLE_BREAKPOINTS.MEDIUM) {
    layoutKey = 'medium';
  } else if (containerWidth >= SIMPLE_BREAKPOINTS.SMALL) {
    layoutKey = 'small';
  } else if (containerWidth >= SIMPLE_BREAKPOINTS.VERY_SMALL) {
    layoutKey = 'verySmall';
  } else if (containerWidth >= SIMPLE_BREAKPOINTS.TWO_LINE) {
    layoutKey = 'twoLine';
  } else {
    layoutKey = 'twoLine'; // Fallback to two-line for very narrow
  }
  
  const layout = SIMPLE_LAYOUTS[layoutKey];
  const buttonSizes = SIMPLE_BUTTON_SIZES[layout.buttonSize];
  
  return {
    ...layout,
    containerWidth,
    buttonSizes,
    // Flex behavior - wrap for two-line layout
    shouldWrap: layout.isMultiLine || containerWidth < SIMPLE_BREAKPOINTS.VERY_SMALL,
    // Minimum heights - taller for two-line layout
    minHeight: layout.isMultiLine ? 70 : layoutKey === 'verySmall' ? 40 : layoutKey === 'small' ? 44 : 48,
    // Use spacer to push controls to the right (not needed in two-line)
    useSpacer: !layout.isMultiLine && containerWidth >= SIMPLE_BREAKPOINTS.MEDIUM,
    // Two-line specific properties
    isTwoLine: layout.isMultiLine || false,
    firstLineElements: layout.firstLineElements || [],
    secondLineElements: layout.secondLineElements || []
  };
};

// Simple hook for container width tracking
export const useSimpleContainerWidth = (containerRef) => {
  const [width, setWidth] = React.useState(0);
  
  React.useEffect(() => {
    if (!containerRef.current) return;
    
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.clientWidth);
      }
    };
    
    // Initial measurement
    updateWidth();
    
    // Use ResizeObserver for efficient updates
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);
    
    return () => resizeObserver.disconnect();
  }, [containerRef]);
  
  return width;
};

// Debug helper
export const getLayoutDebugInfo = (layout) => {
  if (process.env.NODE_ENV !== 'development') return null;
  
  return {
    name: layout.name,
    width: layout.containerWidth,
    breakpoint: layout.containerWidth >= SIMPLE_BREAKPOINTS.LARGE ? 'LARGE' :
                layout.containerWidth >= SIMPLE_BREAKPOINTS.MEDIUM ? 'MEDIUM' :
                layout.containerWidth >= SIMPLE_BREAKPOINTS.SMALL ? 'SMALL' : 'VERY_SMALL',
    visibleElements: {
      timeDisplay: layout.showTimeDisplay,
      volumeSlider: layout.showVolumeSlider,
      downloadButton: layout.showDownloadButton,
      speedControl: layout.showSpeedControl
    }
  };
};
